import clsx from 'clsx';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface LeadScoreBadgeProps {
  score: number | null | undefined;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  className?: string;
}

export function LeadScoreBadge({
  score,
  size = 'md',
  showIcon = false,
  className,
}: LeadScoreBadgeProps) {
  if (score === null || score === undefined) {
    return (
      <span
        className={clsx(
          'inline-flex items-center rounded-full font-medium text-gray-400 bg-gray-100',
          {
            'px-1.5 py-0.5 text-xs': size === 'sm',
            'px-2 py-0.5 text-sm': size === 'md',
            'px-2.5 py-1 text-base': size === 'lg',
          },
          className
        )}
      >
        <Minus className={clsx('mr-0.5', { 'w-3 h-3': size === 'sm', 'w-4 h-4': size !== 'sm' })} />
        --
      </span>
    );
  }

  const getScoreVariant = (s: number) => {
    if (s >= 70) return { bg: 'bg-green-100', text: 'text-green-800', label: 'High' };
    if (s >= 40) return { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Medium' };
    return { bg: 'bg-red-100', text: 'text-red-800', label: 'Low' };
  };

  const variant = getScoreVariant(score);
  const Icon = score >= 70 ? TrendingUp : score >= 40 ? Minus : TrendingDown;

  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full font-semibold',
        variant.bg,
        variant.text,
        {
          'px-1.5 py-0.5 text-xs': size === 'sm',
          'px-2 py-0.5 text-sm': size === 'md',
          'px-2.5 py-1 text-base': size === 'lg',
        },
        className
      )}
      title={`Score: ${score}/100 (${variant.label})`}
    >
      {showIcon && (
        <Icon
          className={clsx('mr-1', {
            'w-3 h-3': size === 'sm',
            'w-4 h-4': size !== 'sm',
          })}
        />
      )}
      {score}
    </span>
  );
}

interface ScoreBreakdownProps {
  breakdown: Record<string, unknown> | null | undefined;
  className?: string;
}

export function ScoreBreakdown({ breakdown, className }: ScoreBreakdownProps) {
  if (!breakdown) {
    return null;
  }

  const typedBreakdown = breakdown as {
    location?: number;
    cuisine?: number;
    license?: number;
    freshness?: number;
    total?: number;
    details?: {
      location_match?: string;
      cuisine_match?: string;
      cuisine_weight?: number;
      license_status?: string;
      days_old?: number;
      freshness_factor?: number;
    };
  };

  const {
    location = 0,
    cuisine = 0,
    license = 0,
    freshness = 0,
    total = 0,
    details = {},
  } = typedBreakdown;

  const components = [
    { label: 'Location', value: location, max: 25, detail: details.location_match },
    { label: 'Cuisine', value: cuisine, max: 35, detail: details.cuisine_match },
    { label: 'License', value: license, max: 20, detail: details.license_status },
    { label: 'Freshness', value: freshness, max: 20, detail: details.days_old !== undefined ? `${details.days_old} days old` : undefined },
  ];

  return (
    <div className={clsx('space-y-2', className)}>
      <div className="flex items-center justify-between text-sm font-medium">
        <span>Total Score</span>
        <LeadScoreBadge score={total} size="sm" showIcon />
      </div>
      <div className="space-y-1">
        {components.map((comp) => (
          <div key={comp.label} className="flex items-center text-xs">
            <span className="w-20 text-gray-500">{comp.label}</span>
            <div className="flex-1 mx-2 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={clsx('h-full rounded-full transition-all', {
                  'bg-green-500': comp.value >= comp.max * 0.7,
                  'bg-yellow-500': comp.value >= comp.max * 0.4 && comp.value < comp.max * 0.7,
                  'bg-red-500': comp.value < comp.max * 0.4,
                })}
                style={{ width: `${(comp.value / comp.max) * 100}%` }}
              />
            </div>
            <span className="w-10 text-right text-gray-600">
              {comp.value}/{comp.max}
            </span>
          </div>
        ))}
      </div>
      {Object.keys(details).length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-200">
          <div className="text-xs text-gray-500 space-y-1">
            {details.location_match && <div>Location: {details.location_match}</div>}
            {details.cuisine_match && <div>Cuisine: {details.cuisine_match}</div>}
            {details.license_status && <div>License: {details.license_status}</div>}
            {details.days_old !== undefined && <div>Age: {details.days_old} days</div>}
          </div>
        </div>
      )}
    </div>
  );
}
